import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";

import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-enterprise";

import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { RiskLevel } from "@components/shared/risk-level-sets/models";
import { TableComponent } from "@components/shared/tables/table/table.component";
import { LinkCellRendererComponent } from "@shared/cell-renderers";
import { TextConstants } from "@shared/constants";
import { RecordStateEnum, TableEnum } from "@shared/enums";
import { NotificationService } from "@shared/services";
import { NavigationParams, RouterService } from "@shared/services/router.service";
import { CellRendererUtils, ColumnUtils, LocalizationUtils } from "@shared/utils";

import { riskAssessmentReportOrderedResourceTypes } from "../../constants";
import {
  RelatedRiskAssessmentReportsTableModel as Model,
  RiskAssessmentReportRecord,
} from "../../models";
import {
  RiskAssessmentReportRelatedRecordsService,
  RiskAssessmentReportsRiskLevelTagsService,
} from "../../services";

@Component({
  selector: "app-related-risk-assessment-reports-table",
  templateUrl: "./related-risk-assessment-reports-table.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RelatedRiskAssessmentReportsTableComponent implements AfterViewInit {
  readonly tagsService = inject(RiskAssessmentReportsRiskLevelTagsService);

  private readonly notification = inject(NotificationService);

  private readonly routerService = inject(RouterService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly relatedRecordsService = inject(RiskAssessmentReportRelatedRecordsService);

  readonly areButtonsEnabled = input(true);

  readonly isFixedBottomPaginator = input(false);

  readonly isPaginatorEnabled = input(true);

  readonly isRecordStateFilterEnabled = input(true);

  readonly isSaveTableState = input(false);

  readonly isSearchEnabled = input(true);

  readonly recordState = input(RecordStateEnum.ALL);

  readonly relatedRecords = input.required<RiskAssessmentReportRecord["record"][]>();

  readonly view = output<RiskAssessmentReportRecord>();

  readonly isLoading = signal(true);

  readonly columnDefs = signal<ColDef[]>([]);

  readonly riskLevels = signal<RiskLevel[]>([]);

  readonly rowData = signal<RiskAssessmentReportRecord[]>([]);

  readonly totalElements = computed(() => this.rowData().length);

  readonly table = TableEnum.RISK_ASSESSMENT_REPORTS;

  readonly rowGroupColumns = signal<string[]>(["record.type"]);

  readonly autoGroupColumnDef = signal<ColDef>({
    headerName: $localize`:@@shared.recordtype:Record type`,
    pinned: null,
    minWidth: 150,
  });

  readonly showTagsFilter = signal(false);

  private readonly tableComponent = viewChild(TableComponent);

  private readonly countryFlag = viewChild("countryFlag", {
    read: TemplateRef<{ $implicit: string }>,
  });

  constructor() {
    this.tagsService.init();

    effect(() => {
      this.tableComponent()?.currentRecordState(); // trigger
      this.showTagsFilter.set(this.tableComponent()?.grid().api.getDisplayedRowCount() > 0);
    });
  }

  async ngAfterViewInit() {
    this.setColumnDefs();

    await this.getAll();
  }

  async getAll() {
    try {
      this.isLoading.set(true);

      const relatedRiskAssessmentReports = await this.loadRelatedRiskAssessmentReports();

      this.rowData.set(relatedRiskAssessmentReports);

      relatedRiskAssessmentReports.forEach((report) => this.tagsService.add(report));

      this.isLoading.set(false);
    } catch (error) {
      this.notification.showError(error);
    }
  }

  onView(row: RiskAssessmentReportRecord) {
    if (this.areButtonsEnabled() && row.id) {
      this.view.emit(row);
    }
  }

  async onSelectedRiskLevel(value: string) {
    const model = this.createFilterModel(value);

    this.tagsService.update(value);

    this.cdr.detectChanges();

    if (this.tableComponent()?.grid()?.api) {
      this.tableComponent()
        .grid()
        .api.setColumnFilterModel<Model.FilterModel>("residualRisk", model);
      this.tableComponent().grid().api.onFilterChanged();
    }
  }

  private createFilterModel(riskLevelValue: string): Model.FilterModel {
    if (riskLevelValue === Model.NONE_RISK_LEVEL.value) {
      return {
        filterType: "text",
        type: "blank",
        filter: null,
      };
    }

    if (riskLevelValue !== Model.ALL_RISK_LEVELS.value) {
      return {
        filterType: "text",
        type: "equals",
        filter: riskLevelValue,
      };
    }

    return null;
  }

  private getLinkRouteFn(
    type: RiskAssessmentTemplateResourceType,
  ): (id?: string, serializeTree?: boolean, extraParams?: any) => NavigationParams | string {
    switch (type) {
      case RiskAssessmentTemplateResourceType.COUNTRY:
        return this.routerService.getCountryLink;
      case RiskAssessmentTemplateResourceType.ORGANISATION:
        return this.routerService.getOrganisationLink;
      case RiskAssessmentTemplateResourceType.LOCATION:
        return this.routerService.getLocationLink;
      case RiskAssessmentTemplateResourceType.MATERIAL:
        return this.routerService.getMaterialLink;
      case RiskAssessmentTemplateResourceType.PRODUCT:
        return this.routerService.getProductLink;
      case RiskAssessmentTemplateResourceType.DELIVERY:
        return this.routerService.getDeliveryLink;
      default:
        return null;
    }
  }

  private setColumnDefs() {
    const tagsColDef = {
      ...ColumnUtils.tags(TextConstants.RESIDUAL_RISK, "residualRisk"),
      filter: "agTextColumnFilter",
      filterParams: {
        readOnly: true,
      },
      filterValueGetter: (params) => {
        if (!params.data?.residualRisk) {
          return null;
        }

        return this.tagsService.getId(params.data.residualRisk);
      },
    };

    const columnDefs: ColDef[] = [
      ColumnUtils.recordState(),
      {
        headerName: TextConstants.TYPE,
        field: "record.type",
        valueFormatter: (params) =>
          LocalizationUtils.getRelatedRiskAssessmentRecordType[params.value] ?? "Unknown",
        rowGroup: true,
        hide: true,
      },
      {
        headerName: TextConstants.NAME_ID,
        field: "record.name",
        valueGetter: (params: ValueGetterParams<RiskAssessmentReportRecord>) =>
          params.node?.group ? "-" : params.data.record.name,
        cellRendererSelector: (params: ICellRendererParams<RiskAssessmentReportRecord>) => {
          if (params.node?.group) {
            return null;
          }

          const data = params.data || params.node?.allLeafChildren?.[0]?.data;
          const isCountryRecord = data.record.type === RiskAssessmentTemplateResourceType.COUNTRY;

          if (data.record?.id) {
            let templateWithContext: { template: TemplateRef<any>; context: any } = null;

            if (isCountryRecord) {
              templateWithContext = {
                template: this.countryFlag(),
                context: {
                  $implicit: data.record.code,
                },
              };
            }

            return {
              component: LinkCellRendererComponent,
              params: {
                templateWithContext,
                icon: "link",
                linkRouteIdParam: "record.id",
                linkRouteFn: this.getLinkRouteFn(data.record.type),
              },
            };
          }

          if (isCountryRecord) {
            return {
              component: () =>
                CellRendererUtils.countryDisplayValue({
                  country: data.record.code,
                  countryName: data.record.name,
                }),
            };
          }

          return null;
        },
      },
      ColumnUtils.dateColumn({
        headerName: TextConstants.DATE_COMPLETED,
        field: "completedDate",
        sort: "desc",
      }),
      tagsColDef,
      ColumnUtils.dateColumn({
        headerName: TextConstants.VALID_UNTIL,
        field: "validUntilDate",
      }),
    ];

    this.columnDefs.set(columnDefs);
  }

  private buildEmptyRelatedRiskAssessmentReport(
    record: RiskAssessmentReportRecord["record"],
  ): RiskAssessmentReportRecord {
    return {
      calculatedRisk: null,
      completedDate: null,
      id: null,
      recordState: RecordStateEnum.ACTIVE,
      recordUris: [],
      referencedReports: [],
      residualRisk: null,
      status: null,
      validUntilDate: null,
      note: null,
      record,
    };
  }

  async loadRelatedRiskAssessmentReports(): Promise<RiskAssessmentReportRecord[]> {
    const reports = await this.relatedRecordsService.getReports(this.relatedRecords());

    const reportIds = new Set(reports.map(({ record }) => record.id));

    for (const record of this.relatedRecords()) {
      if (!reportIds.has(record.id)) {
        reports.push(this.buildEmptyRelatedRiskAssessmentReport(record));
      }
    }

    reports.sort((a, b) => {
      const indexA = riskAssessmentReportOrderedResourceTypes.indexOf(a.record.type);
      const indexB = riskAssessmentReportOrderedResourceTypes.indexOf(b.record.type);

      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    return reports;
  }
}
